import { stagger, type SequenceTime } from "framer-motion";
import type { DomStep, FnStep, Keyframes } from "./choreography";

/**
 * The landing page's transition vocabulary. Each builder returns timeline
 * step(s) for `<Scene timeline>`. Every scene leads with a different signature
 * from this list, so no two sections enter the same way.
 *
 * Shared rules:
 *  - Several matches of one selector never move together: each builder has a
 *    default `stagger`, applied in document order (left → right, top → bottom).
 *  - A step starts when the previous one ends. Pass `at: "-0.2"` to overlap the
 *    previous step's last 0.2s, or `at: "+0.1"` to pause first.
 *  - Builders animate `transform`, so a target must not also carry its own
 *    static transform (rotate-3, translate-*): put that on an inner wrapper.
 *    Likewise no `transition-*` utilities on a target; CSS transitions fight
 *    the per-frame writes and turn the motion to mush.
 *  - A target's LAST keyframe must equal how its CSS already renders it.
 *    Content that appears after the scene was set up (a form re-rendered,
 *    late data) and everything under reduced motion is never animated, so it
 *    shows its CSS look; e.g. an overlay that flashes and fades out needs
 *    `opacity-0` in its classes.
 */

export const EASE = {
  /** Quick start, long soft landing: the default entrance curve. */
  out: [0.22, 1, 0.36, 1],
  /** Symmetric: for wipes and line draws that should feel mechanical. */
  inOut: [0.65, 0, 0.35, 1],
} as const;

export interface StepTiming {
  /** framer-motion sequence `at`: "-0.2", "+0.1", "<" (start with the previous step). */
  at?: SequenceTime;
  /** Seconds per target. */
  duration?: number;
  /** Seconds between successive targets. */
  stagger?: number;
}

/**
 * Per-value override for a quick fade under a springy (or slower) step.
 * Sequences merge it over the step's own transition, so it keeps the step's
 * delay and stagger; the explicit tween stops it inheriting the spring, whose
 * overshoot would make opacity dip and flicker as it settles.
 */
export const opacityTween = (duration: number) => ({ type: "tween" as const, duration, ease: "easeOut" as const });

/* ------------------------------------------------------------------ text */

/** Words climb out from behind their own baseline mask. Target the `[data-word]` spans of `<SplitWords mask>`. */
export function rise(target: string, { at, duration = 0.55, stagger: gap = 0.06 }: StepTiming = {}): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: { y: ["110%", "0%"], rotate: [8, 0] },
    options: { duration, ease: EASE.out, delay: stagger(gap), at },
  };
}

/** Letters bob up one after another, a wave running left → right. Target `<SplitChars>` `[data-char]`. */
export function wave(target: string, { at, duration = 0.6, stagger: gap = 0.026 }: StepTiming = {}): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], y: ["0.75em", "0em"] },
    options: { type: "spring", duration, bounce: 0.55, delay, at, opacity: opacityTween(0.15) },
  };
}

/** Words thump down like rubber stamps: oversized and tilted, then pressed flat. Target `[data-word]`. */
export function stamp(target: string, { at, duration = 0.42, stagger: gap = 0.11 }: StepTiming = {}): DomStep {
  const delay = stagger(gap);
  const times = [0, 0.72, 1];
  return {
    kind: "dom",
    target,
    keyframes: { scale: [1.9, 0.94, 1], rotate: [-10, 0, 0], y: [-18, 0, 0], opacity: [0, 1, 1] },
    options: { duration, times, ease: ["easeIn", "easeOut"], delay, at },
  };
}

/**
 * Typed out character by character with a caret riding the newest letter.
 * Target `<SplitChars>` `[data-char]`; the duration follows the text length.
 */
export function type(target: string, { at, cps = 34 }: { at?: SequenceTime; cps?: number } = {}): FnStep {
  return {
    kind: "fn",
    target,
    render: (chars, p) => {
      const shown = Math.round(p * chars.length);
      chars.forEach((c, i) => {
        c.style.opacity = i < shown ? "1" : "0";
        if (i === shown - 1 && p < 1) c.dataset.caret = "";
        else delete c.dataset.caret;
      });
    },
    options: (chars) => ({ duration: chars.length / cps, ease: "linear", at }),
  };
}

const GLYPHS = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#%&*+=?";

/**
 * Decrypts left → right: a band of random glyphs runs ahead of the resolved
 * text. Target `<SplitChars>` `[data-char]` (they carry the `fx-glyph` class
 * the effect paints through); tint the noise with `--fx-glyph-color`.
 */
export function scramble(
  target: string,
  { at, duration = 1.1, trail = 7 }: { at?: SequenceTime; duration?: number; trail?: number } = {},
): FnStep {
  return {
    kind: "fn",
    target,
    render: (chars, p) => {
      const head = p * (chars.length + trail);
      chars.forEach((c, i) => {
        if (p >= 1 || i < head - trail) delete c.dataset.glyph;
        else if (i < head) {
          // Re-roll only some glyphs each frame so the noise reads as churn, not static.
          if (!c.dataset.glyph || Math.random() < 0.4) {
            c.dataset.glyph = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
          }
        } else c.dataset.glyph = "";
      });
    },
    options: () => ({ duration, ease: "linear", at }),
  };
}

/**
 * A colour block sweeps across, the text appears beneath it at the midpoint,
 * and the block exits to the right. The target needs two children:
 * `[data-block]` (absolute, inset-0, the block colour) and `[data-block-text]`.
 */
export function block(target: string, { at, duration = 0.9 }: StepTiming = {}): DomStep[] {
  // The text step comes first (delayed to the midpoint) and the block joins it
  // with "<", so the NEXT step waits for the block to leave, not for the text.
  return [
    {
      kind: "dom",
      target: `${target} [data-block-text]`,
      keyframes: { opacity: [0, 1] },
      options: { duration: 0.01, delay: duration / 2, at },
    },
    {
      kind: "dom",
      target: `${target} [data-block]`,
      keyframes: { scaleX: [0, 1, 1, 0], originX: [0, 0, 1, 1] },
      options: { duration, times: [0, 0.45, 0.55, 1], ease: [EASE.inOut, "linear", EASE.inOut], at: "<" },
    },
  ];
}

/* --------------------------------------------------------------- reveals */

/**
 * An ink edge sweeps left → right across the target. The clip ends well
 * outside the box, so shadows and descenders survive the reveal.
 */
export function wipe(target: string, { at, duration = 0.6, stagger: gap = 0.12 }: StepTiming = {}): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: {
      clipPath: [
        "inset(-60% 100% -60% 0%)",
        "inset(-60% 0% -60% 0%)",
        "inset(-60% -60% -60% -60%)",
      ],
    },
    options: { duration, times: [0, 0.94, 1], ease: [EASE.inOut, "linear"], delay: stagger(gap), at },
  };
}

/** A circular reveal opening from `origin`, a CSS position inside the target. */
export function iris(
  target: string,
  { at, duration = 0.8, stagger: gap = 0.15, origin = "50% 50%" }: StepTiming & { origin?: string } = {},
): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: { clipPath: [`circle(0% at ${origin})`, `circle(150% at ${origin})`] },
    options: { duration, ease: EASE.out, delay: stagger(gap), at },
  };
}

/** A photo developing like a polaroid: washed out and soft, then full colour. */
export function develop(target: string, { at, duration = 0.9, stagger: gap = 0.22 }: StepTiming = {}): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: {
      filter: ["grayscale(1) brightness(1.55) blur(3px)", "grayscale(0) brightness(1) blur(0px)"],
    },
    options: { duration, ease: "easeOut", delay: stagger(gap), at },
  };
}

/**
 * Draws an SVG stroke from its start. Target the `<path>`/`<line>`/`<circle>`,
 * never the `<svg>`. It takes over the stroke's dash pattern, so to draw a
 * DASHED line, draw a solid stroke inside a `<mask>` laid over the dashed one.
 */
export function draw(target: string, { at, duration = 0.7, stagger: gap = 0.15 }: StepTiming = {}): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: { pathLength: [0, 1], opacity: [0, 1] },
    options: { duration, ease: EASE.inOut, delay, at, opacity: opacityTween(0.05) },
  };
}

/* ---------------------------------------------------------------- motion */

/** Glides in from the left (by default), optionally out of a blur. */
export function slide(
  target: string,
  {
    at,
    duration = 0.5,
    stagger: gap = 0.08,
    x = -48,
    y = 0,
    blur = 0,
  }: StepTiming & { x?: number; y?: number; blur?: number } = {},
): DomStep {
  const keyframes: Keyframes = { opacity: [0, 1] };
  if (x) keyframes.x = [x, 0];
  if (y) keyframes.y = [y, 0];
  if (blur) keyframes.filter = [`blur(${blur}px)`, "blur(0px)"];
  return { kind: "dom", target, keyframes, options: { duration, ease: EASE.out, delay: stagger(gap), at } };
}

/** A quiet fade with a small lift, for supporting details. */
export function fade(
  target: string,
  { at, duration = 0.45, stagger: gap = 0.08, y = 10 }: StepTiming & { y?: number } = {},
): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], y: [y, 0] },
    options: { duration, ease: EASE.out, delay: stagger(gap), at },
  };
}

/** A springy scale-up from a point, for buttons, badges and avatars. */
export function pop(
  target: string,
  { at, duration = 0.55, stagger: gap = 0.1, from = 0.4 }: StepTiming & { from?: number } = {},
): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], scale: [from, 1] },
    options: { type: "spring", duration, bounce: 0.45, delay, at, opacity: opacityTween(0.18) },
  };
}

/** Flies in from the right with a spin that settles, for cards dropped onto the scene. */
export function fly(
  target: string,
  {
    at,
    duration = 0.75,
    stagger: gap = 0.12,
    x = 160,
    y = 40,
    rotate = 14,
  }: StepTiming & { x?: number; y?: number; rotate?: number } = {},
): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], x: [x, 0], y: [y, 0], rotate: [rotate, 0] },
    options: { type: "spring", duration, bounce: 0.3, delay, at, opacity: opacityTween(0.2) },
  };
}

/** Dealt in from off-stage left like cards thrown onto a table. */
export function deal(
  target: string,
  { at, duration = 0.8, stagger: gap = 0.24, x = -900, rotate = -30 }: StepTiming & { x?: number; rotate?: number } = {},
): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], x: [x, 0], y: [70, 0], rotate: [rotate, 0] },
    options: { type: "spring", duration, bounce: 0.22, delay, at, opacity: opacityTween(0.15) },
  };
}

/** Panels turn in on a hinge. With axis "y" put `origin-left` on the target. */
export function flip(
  target: string,
  { at, duration = 0.6, stagger: gap = 0.14, axis = "y", from = -95 }: StepTiming & { axis?: "x" | "y"; from?: number } = {},
): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: {
      opacity: [0, 1],
      [axis === "y" ? "rotateY" : "rotateX"]: [from, 0],
      transformPerspective: [1000, 1000],
    },
    options: { duration, ease: EASE.out, delay, at, opacity: opacityTween(0.25) },
  };
}

/** Rows unfold downward like a folded paper list, hinged on their top edge. */
export function unfold(target: string, { at, duration = 0.5, stagger: gap = 0.12 }: StepTiming = {}): DomStep {
  const delay = stagger(gap);
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], rotateX: [-92, 0], originY: [0, 0], transformPerspective: [800, 800] },
    options: { duration, ease: EASE.out, delay, at, opacity: opacityTween(0.2) },
  };
}

